'use client';

import { useState, useEffect, useCallback } from 'react';

import FilterPanel, { FilterState } from './FilterPanel';

import InvestmentSpecialCard from './InvestmentSpecialCard';
import InvestmentSpecialModal from './InvestmentSpecialModal';
import Icon from '@/components/ui/AppIcon';

interface Deal {
  id: string;
  title: string;
  category: string;
  completionDate: string;
  investmentAmount: string;
  roi: string;
  roiPercentage: number;
  status: 'completed' | 'verified' | 'settled';
  image: string;
  alt: string;
  clientType: string;
  strategy: string;
  duration: string;
  highlights: string[];
  description: string;
  timeline: Array<{
    date: string;
    event: string;
    description: string;
  }>;
  metrics: Array<{
    label: string;
    value: string;
    icon: string;
  }>;
}

interface InvestmentSpecial {
  id: string;
  title: string;
  category: string;
  minInvestment: string;
  targetRoi: string;
  roiPercentage: number;
  availability: 'limited' | 'open' | 'closing-soon';
  image: string;
  alt: string;
  riskLevel: 'no-risk' | 'low' | 'medium' | 'high';
  duration: string;
  spotsAvailable: number;
  highlights: string[];
  deadline: string;
  description: string;
  terms: string[];
  contactInfo: string;
}

const DailyDealsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredDeals, setFilteredDeals] = useState<Deal[]>([]);
  const [selectedSpecial, setSelectedSpecial] = useState<InvestmentSpecial | null>(null);
  const [isSpecialModalOpen, setIsSpecialModalOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const investmentSpecials: InvestmentSpecial[] = [
  {
    id: 'special-001',
    title: 'Starter Investment Package',
    category: 'Entry Level',
    minInvestment: '$150',
    targetRoi: '$8,000',
    roiPercentage: 5233,
    availability: 'open',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb57bbc1-1770661170152.png",
    alt: 'Entry-level investment opportunity with high returns for new investors',
    riskLevel: 'no-risk',
    duration: 'profit available in 25 minutes',
    spotsAvailable: 25,
    deadline: 'Mar 09, 2026',
    highlights: [
    'Perfect for first-time investors',
    'Low entry barrier with exceptional returns',
    'Comprehensive investor education included',
    'Monthly progress updates and support'],

    description: 'Start your investment journey with our exclusive starter package. Invest just $150 and receive $8,000 in returns through our proven investment strategies and expert portfolio management.',
    terms: [
    'Minimum investment: $150',
    'Expected return: $8,000',
    'Investment period: 12-18 months',
    'No hidden fees or management charges',
    'Full transparency with monthly reporting'],

    contactInfo: 'investments@inversionrapida.com | +1 (555) 123-4567'
  },
  {
    id: 'special-002',
    title: 'Growth Investment Package',
    category: 'Intermediate',
    minInvestment: '$200',
    targetRoi: '$10,000',
    roiPercentage: 4900,
    availability: 'limited',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_144d697a5-1770661168044.png",
    alt: 'Growth-focused investment package with accelerated returns',
    riskLevel: 'no-risk',
    duration: 'profit available in 25 minutes',
    spotsAvailable: 20,
    deadline: 'Mar 09, 2026',
    highlights: [
    'Enhanced return potential for growing portfolios',
    'Diversified investment across multiple sectors',
    'Priority access to exclusive opportunities',
    'Dedicated account manager support'],

    description: 'Accelerate your wealth building with our growth package. A $200 investment grows to $10,000 through strategic market positioning and expert fund management.',
    terms: [
    'Minimum investment: $200',
    'Expected return: $10,000',
    'Investment period: 12-18 months',
    'Priority investor status',
    'Quarterly performance reviews'],

    contactInfo: 'investments@inversionrapida.com | +1 (555) 123-4567'
  },
  {
    id: 'special-003',
    title: 'Premium Investment Package',
    category: 'Advanced',
    minInvestment: '$300',
    targetRoi: '$15,000',
    roiPercentage: 4900,
    availability: 'open',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_144d697a5-1770661168044.png",
    alt: 'Premium investment package with substantial returns and exclusive benefits',
    riskLevel: 'no-risk',
    duration: 'profit available in 25 minutes',
    spotsAvailable: 15,
    deadline: 'Mar 09, 2026',
    highlights: [
    'Premium tier with enhanced return structure',
    'Access to institutional-grade investments',
    'VIP investor events and networking',
    'Personalized investment strategy consultation'],

    description: 'Join our premium investors with a $300 commitment that delivers $15,000 in returns. Benefit from exclusive access to high-performing investment opportunities and personalized wealth management.',
    terms: [
    'Minimum investment: $300',
    'Expected return: $15,000',
    'Investment period: profit available in 25 minutes',
    'VIP investor benefits included',
    'Bi-weekly portfolio updates'],

    contactInfo: 'investments@inversionrapida.com | +1 (555) 123-4568'
  },
  {
    id: 'special-004',
    title: 'Elite Investment Package',
    category: 'Professional',
    minInvestment: '$400',
    targetRoi: '$18,000',
    roiPercentage: 4400,
    availability: 'closing-soon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb57bbc1-1770661170152.png",
    alt: 'Elite investment package for serious investors seeking maximum returns',
    riskLevel: 'no-risk',
    duration: 'profit available in 25 minutes',
    spotsAvailable: 10,
    deadline: 'Mar 09, 2026',
    highlights: [
    'Elite tier with superior return potential',
    'Direct access to fund managers',
    'Exclusive early access to new opportunities',
    'Comprehensive wealth planning services'],

    description: 'Our elite package transforms a $400 investment into $18,000 through strategic positioning in high-growth markets and expert portfolio optimization.',
    terms: [
    'Minimum investment: $400',
    'Expected return: $18,000',
    'Investment period: profit available in 25 minutes',
    'Elite investor privileges',
    'Weekly market insights and updates'],

    contactInfo: 'investments@inversionrapida.com | +1 (555) 123-4569'
  },
  {
    id: 'special-005',
    title: 'Platinum Investment Package',
    category: 'Executive',
    minInvestment: '$500',
    targetRoi: '$25,000',
    roiPercentage: 4900,
    availability: 'limited',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7369a09-1770914682902.png",
    alt: 'Platinum executive investment package with exceptional returns and white-glove service',
    riskLevel: 'no-risk',
    duration: 'profit available in 25 minutes',
    spotsAvailable: 8,
    deadline: 'Mar 09, 2026',
    highlights: [
    'Highest tier with maximum return potential',
    'White-glove concierge investment service',
    'First access to all premium opportunities',
    'Complete wealth management suite'],

    description: 'Our platinum package represents the pinnacle of investment opportunity. Transform $500 into $25,000 with our most exclusive investment strategies and comprehensive wealth management services.',
    terms: [
    'Minimum investment: $500',
    'Expected return: $25,000',
    'Investment period: 18-24 months',
    'Platinum investor status with all benefits',
    'Daily market briefings and priority support'],

    contactInfo: 'investments@inversionrapida.com | +1 (555) 123-4570'
  }];


  const mockDeals: Deal[] = [
  {
    id: 'deal-001',
    title: 'Downtown Commercial Plaza Acquisition',
    category: 'Real Estate',
    completionDate: 'Jan 12, 2026',
    investmentAmount: '$2.4M',
    roi: '+32.5%',
    roiPercentage: 32.5,
    status: 'completed',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17ef18b31-1768149206338.png",
    alt: 'Modern glass and steel commercial building with multiple floors in downtown business district',
    clientType: 'Family Office',
    strategy: 'Growth',
    duration: '18 months',
    highlights: [
    'Prime downtown location with high foot traffic',
    'Secured anchor tenant with 10-year lease',
    'Property value increased 28% during renovation',
    'Generated 4.2% annual rental yield above projections'],

    description: 'Strategic acquisition of a 45,000 sq ft commercial plaza in the heart of downtown business district. The property underwent comprehensive renovation and repositioning, attracting premium tenants and achieving full occupancy within 6 months of completion.',
    timeline: [
    { date: 'Jul 2024', event: 'Property Acquisition', description: 'Closed deal at $2.4M with favorable financing terms' },
    { date: 'Aug 2024', event: 'Renovation Started', description: 'Comprehensive modernization of common areas and facilities' },
    { date: 'Nov 2024', event: 'Anchor Tenant Secured', description: 'Major tech company signed 10-year lease for 15,000 sq ft' },
    { date: 'Jan 2026', event: 'Full Occupancy', description: 'All units leased with premium tenants, deal completed' }],

    metrics: [
    { label: 'Investment', value: '$2.4M', icon: 'CurrencyDollarIcon' },
    { label: 'ROI', value: '32.5%', icon: 'ChartBarIcon' },
    { label: 'Duration', value: '18 mo', icon: 'ClockIcon' },
    { label: 'Occupancy', value: '100%', icon: 'BuildingOfficeIcon' }]

  },
  {
    id: 'deal-002',
    title: 'AI Healthcare Diagnostics Series B',
    category: 'Technology',
    completionDate: 'Jan 11, 2026',
    investmentAmount: '$1.8M',
    roi: '+45.2%',
    roiPercentage: 45.2,
    status: 'verified',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10be8be52-1764675862266.png",
    alt: 'Medical professional using advanced AI diagnostic equipment with digital screens showing patient data',
    clientType: 'High Net Worth',
    strategy: 'Growth',
    duration: '24 months',
    highlights: [
    'FDA approval received for flagship diagnostic tool',
    'Partnership with 3 major hospital networks',
    'Revenue grew 340% year-over-year',
    'Successful Series C funding at 2.8x valuation'],

    description: 'Series B investment in cutting-edge AI-powered healthcare diagnostics platform. The company developed revolutionary early detection algorithms for multiple conditions, achieving FDA approval and rapid market adoption.',
    timeline: [
    { date: 'Jan 2024', event: 'Series B Investment', description: 'Led $8M round with $1.8M commitment' },
    { date: 'May 2024', event: 'FDA Approval', description: 'Flagship product received regulatory clearance' },
    { date: 'Sep 2024', event: 'Hospital Partnerships', description: 'Secured contracts with major healthcare networks' },
    { date: 'Jan 2026', event: 'Series C Exit', description: 'Partial exit at 2.8x valuation multiple' }],

    metrics: [
    { label: 'Investment', value: '$1.8M', icon: 'CurrencyDollarIcon' },
    { label: 'ROI', value: '45.2%', icon: 'ChartBarIcon' },
    { label: 'Duration', value: '24 mo', icon: 'ClockIcon' },
    { label: 'Valuation', value: '2.8x', icon: 'ArrowTrendingUpIcon' }]

  },
  {
    id: 'deal-003',
    title: 'Renewable Energy Infrastructure Fund',
    category: 'Energy',
    completionDate: 'Jan 10, 2026',
    investmentAmount: '$3.2M',
    roi: '+28.7%',
    roiPercentage: 28.7,
    status: 'settled',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb225bea-1765646429143.png",
    alt: 'Large solar panel farm with rows of photovoltaic panels under blue sky in rural landscape',
    clientType: 'Institutional',
    strategy: 'Income',
    duration: '36 months',
    highlights: [
    'Portfolio of 12 solar installations across 5 states',
    'Long-term power purchase agreements secured',
    'Consistent 6.8% annual dividend payments',
    'Government incentives maximized for tax benefits'],

    description: 'Strategic investment in diversified renewable energy infrastructure fund focusing on utility-scale solar installations. The fund achieved stable cash flows through long-term PPAs while benefiting from favorable regulatory environment.',
    timeline: [
    { date: 'Jan 2023', event: 'Fund Commitment', description: 'Invested $3.2M in infrastructure fund' },
    { date: 'Jun 2023', event: 'First Projects Online', description: '4 solar farms began commercial operation' },
    { date: 'Dec 2024', event: 'Full Portfolio Active', description: 'All 12 installations operational and generating revenue' },
    { date: 'Jan 2026', event: 'Fund Distribution', description: 'Successful exit with 28.7% total return' }],

    metrics: [
    { label: 'Investment', value: '$3.2M', icon: 'CurrencyDollarIcon' },
    { label: 'ROI', value: '28.7%', icon: 'ChartBarIcon' },
    { label: 'Duration', value: '36 mo', icon: 'ClockIcon' },
    { label: 'Yield', value: '6.8%', icon: 'BoltIcon' }]

  },
  {
    id: 'deal-004',
    title: 'Biotech Drug Development Partnership',
    category: 'Healthcare',
    completionDate: 'Jan 9, 2026',
    investmentAmount: '$2.1M',
    roi: '+52.3%',
    roiPercentage: 52.3,
    status: 'completed',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_194a00a7b-1767253293561.png",
    alt: 'Scientists in white lab coats working with advanced laboratory equipment and microscopes in modern research facility',
    clientType: 'Family Office',
    strategy: 'Growth',
    duration: '30 months',
    highlights: [
    'Phase 3 clinical trials exceeded efficacy targets',
    'Breakthrough therapy designation from FDA',
    'Acquisition by major pharmaceutical company',
    'Multiple patent protections secured globally'],

    description: 'Early-stage investment in biotech company developing novel treatment for autoimmune disorders. The therapy demonstrated exceptional results in clinical trials, leading to acquisition by Big Pharma at significant premium.',
    timeline: [
    { date: 'Jul 2023', event: 'Initial Investment', description: 'Participated in Series A with $2.1M commitment' },
    { date: 'Jan 2024', event: 'Phase 2 Success', description: 'Clinical trials showed promising safety and efficacy' },
    { date: 'Aug 2024', event: 'FDA Designation', description: 'Received breakthrough therapy status' },
    { date: 'Jan 2026', event: 'Acquisition Completed', description: 'Acquired by pharmaceutical giant at premium valuation' }],

    metrics: [
    { label: 'Investment', value: '$2.1M', icon: 'CurrencyDollarIcon' },
    { label: 'ROI', value: '52.3%', icon: 'ChartBarIcon' },
    { label: 'Duration', value: '30 mo', icon: 'ClockIcon' },
    { label: 'Exit Multiple', value: '3.2x', icon: 'BeakerIcon' }]

  },
  {
    id: 'deal-005',
    title: 'Fintech Payment Platform Series C',
    category: 'Finance',
    completionDate: 'Jan 8, 2026',
    investmentAmount: '$1.5M',
    roi: '+38.9%',
    roiPercentage: 38.9,
    status: 'verified',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1eff135a3-1766601351747.png",
    alt: 'Modern smartphone displaying digital payment app interface with credit cards and financial transaction graphics',
    clientType: 'High Net Worth',
    strategy: 'Balanced',
    duration: '20 months',
    highlights: [
    'User base grew from 2M to 8M during investment period',
    'Expanded to 15 new international markets',
    'Strategic partnership with major banking consortium',
    'Revenue increased 280% with improved unit economics'],

    description: 'Series C investment in rapidly growing fintech payment platform serving SMB merchants. The company achieved remarkable user growth and market expansion while maintaining strong unit economics and regulatory compliance.',
    timeline: [
    { date: 'May 2024', event: 'Series C Investment', description: 'Joined $45M round with $1.5M allocation' },
    { date: 'Aug 2024', event: 'Banking Partnership', description: 'Secured strategic alliance with top-tier banks' },
    { date: 'Nov 2024', event: 'International Expansion', description: 'Launched in 15 new markets across 3 continents' },
    { date: 'Jan 2026', event: 'Secondary Sale', description: 'Partial liquidity event at increased valuation' }],

    metrics: [
    { label: 'Investment', value: '$1.5M', icon: 'CurrencyDollarIcon' },
    { label: 'ROI', value: '38.9%', icon: 'ChartBarIcon' },
    { label: 'Duration', value: '20 mo', icon: 'ClockIcon' },
    { label: 'Users', value: '8M', icon: 'UserGroupIcon' }]

  },
  {
    id: 'deal-006',
    title: 'Luxury Residential Development',
    category: 'Real Estate',
    completionDate: 'Jan 7, 2026',
    investmentAmount: '$4.2M',
    roi: '+26.4%',
    roiPercentage: 26.4,
    status: 'completed',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18c6d33c5-1769432645647.png",
    alt: 'Elegant luxury apartment interior with floor-to-ceiling windows, modern furniture, and city skyline view',
    clientType: 'Institutional',
    strategy: 'Value',
    duration: '28 months',
    highlights: [
    '24 luxury condominiums in prime waterfront location',
    'All units pre-sold before construction completion',
    'Premium pricing achieved 15% above market average',
    'Sustainable design features attracted eco-conscious buyers'],

    description: 'Development investment in boutique luxury residential project featuring high-end amenities and sustainable design. Strong market demand and premium positioning resulted in rapid sell-through at above-market pricing.',
    timeline: [
    { date: 'Sep 2023', event: 'Project Investment', description: 'Committed $4.2M to development partnership' },
    { date: 'Dec 2023', event: 'Construction Started', description: 'Groundbreaking ceremony and foundation work began' },
    { date: 'Jun 2025', event: 'Pre-Sales Launched', description: 'All 24 units sold within 3 months of launch' },
    { date: 'Jan 2026', event: 'Project Completion', description: 'Final units delivered and investment realized' }],

    metrics: [
    { label: 'Investment', value: '$4.2M', icon: 'CurrencyDollarIcon' },
    { label: 'ROI', value: '26.4%', icon: 'ChartBarIcon' },
    { label: 'Duration', value: '28 mo', icon: 'ClockIcon' },
    { label: 'Units', value: '24', icon: 'HomeIcon' }]

  }];


  useEffect(() => {
    if (isHydrated) {
      setFilteredDeals(mockDeals);
    }
  }, [isHydrated]);

  const handleFilterChange = useCallback((filters: FilterState) => {
    let filtered = [...mockDeals];

    if (filters.category !== 'all') {
      filtered = filtered.filter((deal) => deal.category.toLowerCase() === filters.category);
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter((deal) => deal.status === filters.status);
    }

    if (filters.strategy !== 'all') {
      filtered = filtered.filter((deal) => deal.strategy.toLowerCase() === filters.strategy);
    }

    if (filters.sortBy === 'roi-high') {
      filtered.sort((a, b) => b.roiPercentage - a.roiPercentage);
    } else if (filters.sortBy === 'roi-low') {
      filtered.sort((a, b) => a.roiPercentage - b.roiPercentage);
    }

    setFilteredDeals(filtered);
  }, []);

  const handleViewDetails = (dealId: string) => {
    const deal = mockDeals.find((d) => d.id === dealId);
    if (deal) {
      setSelectedDeal(deal);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDeal(null);
  };

  const handleLearnMore = (specialId: string) => {
    const special = investmentSpecials.find((s) => s.id === specialId);
    if (special) {
      setSelectedSpecial(special);
      setIsSpecialModalOpen(true);
    }
  };

  const handleCloseSpecialModal = () => {
    setIsSpecialModalOpen(false);
    setSelectedSpecial(null);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3"></div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="h-96 bg-muted rounded"></div>
              <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) =>
                <div key={i} className="h-96 bg-muted rounded"></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <>
      {/* Investment Specials Section */}
      <div className="mb-16">

        {/* Investment Deals Bracket - All deals in one clear container */}
        <div className="bg-card rounded-2xl border-2 border-primary/30 shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-primary to-primary/80 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="BriefcaseIcon" size={24} variant="solid" className="text-primary-foreground" />
                <h3 className="text-xl font-heading font-bold text-primary-foreground">Available Investment Deals</h3>
              </div>
              <div className="bg-primary-foreground/20 px-4 py-1 rounded-full">
                <span className="text-sm font-semibold text-primary-foreground">{investmentSpecials.length} Packages</span>
              </div>
            </div>
          </div>
          
          <div className="p-6 bg-gradient-to-br from-card to-muted/20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {investmentSpecials.map((special) =>
              <InvestmentSpecialCard
                key={special.id}
                special={special}
                onLearnMore={handleLearnMore} />

              )}
            </div>
          </div>
        </div>

        {/* Key Metrics Section - Now positioned under Available Investment Deals */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-card rounded-xl p-6 border border-border shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-primary" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Total Deals Completed</h3>
            <p className="text-3xl font-bold text-foreground">247</p>
          </div>

          <div className="bg-card rounded-xl p-6 border border-border shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Icon name="CurrencyDollarIcon" size={24} variant="solid" className="text-green-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Total Investment Value</h3>
            <p className="text-3xl font-bold text-foreground">$42.8M</p>
          </div>

          <div className="bg-card rounded-xl p-6 border border-border shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Icon name="ChartBarIcon" size={24} variant="solid" className="text-blue-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Average ROI</h3>
            <p className="text-3xl font-bold text-foreground">38.4%</p>
          </div>

          <div className="bg-card rounded-xl p-6 border border-border shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Icon name="UserGroupIcon" size={24} variant="solid" className="text-purple-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Active Clients</h3>
            <p className="text-3xl font-bold text-foreground">1,284</p>
          </div>
        </div>
      </div>

      <InvestmentSpecialModal special={selectedSpecial} isOpen={isSpecialModalOpen} onClose={handleCloseSpecialModal} />
    </>);


};

export default DailyDealsInteractive;